import React, { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  AppBar,
  Avatar,
  Box,
  Button,
  ButtonBase,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  ScopedCssBaseline,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { alpha, ThemeProvider, useTheme } from "@mui/material/styles";
import BugReportOutlinedIcon from "@mui/icons-material/BugReportOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";

import logo from "../../static/images/Logo.webp";
import supportAdminTheme, { brand } from "./adminTheme";
import UserLookupPanel from "./UserLookupPanel";
import LicenseLookupPanel from "./LicenseLookupPanel";
import ActivationLookupPanel from "./ActivationLookupPanel";
import { api, errorMessage, setInvalidSessionHandler } from "./api";
import { useSupportRobotsMetadata } from "./metadata";
import {
  BugReportDetail,
  BugReportExportOption,
  BugReportSummary,
  DashboardSummary,
  DiscountCodeResult,
  ProductCatalogItem,
  ProductKeyResult,
  SupportReply,
  SupportSession,
  TicketDetail,
  TicketState,
  TicketSummary,
} from "./types";


type Section = "tickets" | "bugs" | "keys" | "discounts" | "users" | "licenses" | "activations";
type SummaryTone = "cyan" | "violet" | "emerald" | "amber";

const summaryTones: SummaryTone[] = ["cyan", "violet", "emerald", "amber"];
const summaryToneColors: Record<SummaryTone, string> = {
  cyan: brand.cyan,
  violet: brand.violet,
  emerald: brand.emerald,
  amber: brand.amber,
};

const loginFieldSx = {
  mt: 2.25,
  width: "100%",
};

const responsivePaginationSx = {
  "& .MuiTablePagination-toolbar": {
    minHeight: { xs: 48, sm: 52 },
    px: { xs: 0, sm: 2 },
  },
  "& .MuiTablePagination-spacer": { display: { xs: "none", sm: "block" } },
  "& .MuiTablePagination-displayedRows": { ml: { xs: "auto", sm: 0 } },
};

function uuid(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const value = Array.from(bytes, byte => byte.toString(16).padStart(2, "0")).join("");
  return `${value.slice(0, 8)}-${value.slice(8, 12)}-${value.slice(12, 16)}-${value.slice(
    16,
    20
  )}-${value.slice(20)}`;
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(
    new Date(value)
  );
}

function replyStatusLabel(status: SupportReply["status"]) {
  if (status === "sent") return "Accepted for delivery";
  if (status === "unknown") return "Delivery uncertain";
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function discountCodeStatus(code: DiscountCodeResult) {
  if (code.deleted) return "Deleted";
  if (code.expires_at && new Date(code.expires_at).getTime() <= Date.now()) return "Expired";
  if (code.max_redemptions > 0 && code.times_redeemed >= code.max_redemptions) {
    return "Exhausted";
  }
  return code.active ? "Active" : "Disabled";
}

function LoadingScreen() {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        bgcolor: "background.default",
        backgroundImage: `linear-gradient(180deg, ${alpha(brand.accent, 0.035)}, transparent 55%)`,
      }}
      aria-label="Checking support authorization"
    >
      <Stack spacing={2.5} alignItems="center">
        <Box component="img" src={logo} alt="WaveMind" sx={{ width: 124, height: "auto" }} />
        <CircularProgress color="secondary" size={28} thickness={3} aria-label="Loading" />
      </Stack>
    </Box>
  );
}

function Login({ onLogin }: { onLogin: (session: SupportSession) => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      onLogin(await api.login(email, password));
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setSubmitting(false);
      setPassword("");
    }
  };

  return (
    <Box
      component="main"
      sx={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        position: "relative",
        overflow: "hidden",
        bgcolor: "background.default",
        px: { xs: 2, sm: 3 },
        py: 4,
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          background: `linear-gradient(135deg, ${alpha(brand.accent, 0.045)}, ${alpha(brand.violet, 0.025)})`,
        },
      }}
    >
      <Stack
        spacing={3}
        alignItems="center"
        sx={{ width: "100%", maxWidth: 440, position: "relative", zIndex: 1 }}
      >
        <Box component="img" src={logo} alt="WaveMind" sx={{ width: 148, height: "auto" }} />
        <Card
          component="form"
          onSubmit={submit}
          sx={{
            width: "100%",
            position: "relative",
            overflow: "hidden",
            borderRadius: 2,
            "&::before": {
              content: '""',
              position: "absolute",
              inset: "0 0 auto",
              height: 2,
              backgroundColor: alpha(brand.accent, 0.72),
            },
          }}
        >
          <CardContent sx={{ p: { xs: 3, sm: 4 }, "&:last-child": { pb: { xs: 3, sm: 4 } } }}>
            <Typography component="h1" variant="h2" textAlign="center">
              Support Login
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 0.75, textAlign: "center" }}>
              Private access for WaveMind support administrators.
            </Typography>
            {error && (
              <Alert severity="error" sx={{ mt: 2.5, width: "100%" }} aria-live="assertive">
                {error}
              </Alert>
            )}
            <TextField
              label="Email"
              type="email"
              autoComplete="username"
              required
              fullWidth
              sx={loginFieldSx}
              value={email}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setEmail(event.target.value)
              }
            />
            <TextField
              label="Password"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              required
              fullWidth
              sx={loginFieldSx}
              value={password}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setPassword(event.target.value)
              }
              InputProps={{
                endAdornment: (
                  <IconButton
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    color="inherit"
                    edge="end"
                    onClick={() => setShowPassword(current => !current)}
                  >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                ),
              }}
            />
            <Button
              type="submit"
              sx={{ mt: 2.5, width: "100%" }}
              variant="contained"
              color="primary"
              disabled={submitting}
            >
              {submitting ? "Logging in…" : "Login"}
            </Button>
          </CardContent>
        </Card>
        <Typography variant="caption" color="text.secondary">
          WaveMind private support
        </Typography>
      </Stack>
    </Box>
  );
}

interface DashboardProps {
  session: SupportSession;
  onSession: (session: SupportSession) => void;
  onLogout: () => void;
}

function Dashboard({ session, onSession, onLogout }: DashboardProps) {
  const { administrator, csrf_token: csrf } = session;
  const theme = useTheme();
  const desktopNavigation = useMediaQuery(theme.breakpoints.up("md"));
  const [section, setSection] = useState<Section>("tickets");
  const [userLookupEmail, setUserLookupEmail] = useState("");
  const [licenseLookupKey, setLicenseLookupKey] = useState("");
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [ticketState, setTicketState] = useState<TicketState>("open");
  const [ticketSearch, setTicketSearch] = useState("");
  const [ticketQuery, setTicketQuery] = useState("");
  const [ticketPage, setTicketPage] = useState(0);
  const [ticketRows, setTicketRows] = useState<TicketSummary[]>([]);
  const [ticketTotal, setTicketTotal] = useState(0);
  const [ticketDetail, setTicketDetail] = useState<TicketDetail | null>(null);

  const [bugState, setBugState] = useState<TicketState>("open");
  const [bugSearch, setBugSearch] = useState("");
  const [bugQuery, setBugQuery] = useState("");
  const [bugPage, setBugPage] = useState(0);
  const [bugRows, setBugRows] = useState<BugReportSummary[]>([]);
  const [bugTotal, setBugTotal] = useState(0);
  const [bugDetail, setBugDetail] = useState<BugReportDetail | null>(null);
  const [bugExportOptions, setBugExportOptions] = useState<BugReportExportOption[]>([]);
  const [bugExportProduct, setBugExportProduct] = useState("");
  const [bugExportVersion, setBugExportVersion] = useState("");
  const [bugExportOptionsLoading, setBugExportOptionsLoading] = useState(false);
  const [exportingBugs, setExportingBugs] = useState(false);

  const [replyText, setReplyText] = useState("");
  const [closeAfterSend, setCloseAfterSend] = useState(true);
  const [sending, setSending] = useState(false);
  const [confirmSend, setConfirmSend] = useState(false);
  const [pendingReplyAttempt, setPendingReplyAttempt] = useState<{
    ticketId: string;
    responseText: string;
    closeAfterSend: boolean;
    previousReplyId?: string;
    idempotencyKey: string;
  } | null>(null);
  const [bugReplyText, setBugReplyText] = useState("");
  const [bugCloseAfterSend, setBugCloseAfterSend] = useState(true);
  const [bugSending, setBugSending] = useState(false);
  const [confirmBugSend, setConfirmBugSend] = useState(false);
  const [pendingBugReplyAttempt, setPendingBugReplyAttempt] = useState<{
    reportId: string;
    responseText: string;
    closeAfterSend: boolean;
    previousReplyId?: string;
    idempotencyKey: string;
  } | null>(null);

  const [catalog, setCatalog] = useState<ProductCatalogItem[]>([]);
  const [selectedProduct, setSelectedProduct] = useState("");
  const [maxLicenses, setMaxLicenses] = useState(3);
  const [claimKey, setClaimKey] = useState<ProductKeyResult | null>(null);
  const [issuing, setIssuing] = useState(false);
  const [reauthOpen, setReauthOpen] = useState(false);
  const [reauthPassword, setReauthPassword] = useState("");
  const [pendingKeyRequest, setPendingKeyRequest] = useState<{
    productId: string;
    maxLicenses: number;
    idempotencyKey: string;
  } | null>(null);

  const [discountName, setDiscountName] = useState("");
  const [customDiscountCode, setCustomDiscountCode] = useState("");
  const [discountPercentage, setDiscountPercentage] = useState(20);
  const [discountMaxRedemptions, setDiscountMaxRedemptions] = useState(1);
  const [discountDurationHours, setDiscountDurationHours] = useState(168);
  const [discountCode, setDiscountCode] = useState<DiscountCodeResult | null>(null);
  const [discountRows, setDiscountRows] = useState<DiscountCodeResult[]>([]);
  const [discountsLoading, setDiscountsLoading] = useState(false);
  const [creatingDiscount, setCreatingDiscount] = useState(false);
  const [discountActionBusyId, setDiscountActionBusyId] = useState("");
  const [pendingDiscountRequest, setPendingDiscountRequest] = useState<{
    name: string;
    customCode?: string;
    percentage: number;
    maxRedemptions: number;
    expiresAt: string;
    idempotencyKey: string;
  } | null>(null);
  const [pendingDiscountAction, setPendingDiscountAction] = useState<{
    promotionCodeId: string;
    action: "disable" | "delete";
    idempotencyKey: string;
  } | null>(null);

  const loadSummary = useCallback(async () => {
    try {
      setSummary(await api.summary());
    } catch (requestError) {
      setError(errorMessage(requestError));
    }
  }, []);

  const loadTickets = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await api.queryTickets(csrf, ticketState, ticketQuery, ticketPage + 1, 25);
      setTicketRows(result.items);
      setTicketTotal(result.total);
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }, [csrf, ticketPage, ticketQuery, ticketState]);

  const loadBugs = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await api.queryBugReports(csrf, bugState, bugQuery, bugPage + 1, 25);
      setBugRows(result.items);
      setBugTotal(result.total);
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }, [bugPage, bugQuery, bugState, csrf]);

  const loadBugExportOptions = useCallback(async () => {
    setBugExportOptionsLoading(true);
    try {
      const items = await api.bugReportExportOptions();
      setBugExportOptions(items);
      setBugExportProduct(current => current || items[0]?.product_id || "");
      setBugExportVersion(current => current || items[0]?.product_version || "");
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setBugExportOptionsLoading(false);
    }
  }, []);

  const bugExportProducts = useMemo(
    () => Array.from(new Set(bugExportOptions.map(option => option.product_id))),
    [bugExportOptions]
  );
  const bugExportVersions = useMemo(
    () => bugExportOptions.filter(option => option.product_id === bugExportProduct),
    [bugExportOptions, bugExportProduct]
  );

  const loadDiscountCodes = useCallback(async () => {
    setDiscountsLoading(true);
    try {
      setDiscountRows(await api.discountCodes(csrf));
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setDiscountsLoading(false);
    }
  }, [csrf]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);
  useEffect(() => {
    if (section === "tickets") loadTickets();
  }, [loadTickets, section]);
  useEffect(() => {
    if (section === "bugs") loadBugs();
  }, [loadBugs, section]);
  useEffect(() => {
    if (section === "bugs" && bugExportOptions.length === 0) loadBugExportOptions();
  }, [bugExportOptions.length, loadBugExportOptions, section]);
  useEffect(() => {
    if (
      bugExportProduct &&
      !bugExportVersions.some(option => option.product_version === bugExportVersion)
    ) {
      setBugExportVersion(bugExportVersions[0]?.product_version || "");
    }
  }, [bugExportProduct, bugExportVersion, bugExportVersions]);
  useEffect(() => {
    if (section === "discounts" && administrator.role === "manager") loadDiscountCodes();
  }, [administrator.role, loadDiscountCodes, section]);
  useEffect(() => {
    if (section === "keys" && administrator.role === "manager" && catalog.length === 0) {
      api
        .catalog()
        .then(items => {
          setCatalog(items);
          setSelectedProduct(current => current || items[0]?.id || "");
        })
        .catch(requestError => setError(errorMessage(requestError)));
    }
    if (section !== "keys") setClaimKey(null);
  }, [administrator.role, catalog.length, section]);
  useEffect(() => {
    if (section !== "discounts") setDiscountCode(null);
  }, [section]);

  const refreshAll = async () => {
    await Promise.all([
      loadSummary(),
      section === "tickets"
        ? loadTickets()
        : section === "bugs"
          ? loadBugs()
          : section === "discounts" && administrator.role === "manager"
            ? loadDiscountCodes()
            : Promise.resolve(),
      section === "bugs" ? loadBugExportOptions() : Promise.resolve(),
    ]);
  };

  const signOut = async () => {
    try {
      await api.logout(csrf);
    } catch {
      // Local support state is still cleared when the server is unavailable.
    } finally {
      setClaimKey(null);
      setDiscountCode(null);
      setDiscountRows([]);
      onLogout();
    }
  };

  const openTicket = async (id: string) => {
    setError("");
    try {
      setTicketDetail(await api.ticket(id));
      setReplyText("");
      setCloseAfterSend(true);
    } catch (requestError) {
      setError(errorMessage(requestError));
    }
  };

  const openBug = async (id: string) => {
    setError("");
    try {
      setBugDetail(await api.bugReport(id));
      setBugReplyText("");
      setBugCloseAfterSend(true);
    } catch (requestError) {
      setError(errorMessage(requestError));
    }
  };

  const exportBugs = async () => {
    if (!bugExportProduct || !bugExportVersion) return;
    setExportingBugs(true);
    setError("");
    try {
      await api.exportBugReports(bugExportProduct, bugExportVersion, csrf);
      const count =
        bugExportOptions.find(
          option =>
            option.product_id === bugExportProduct &&
            option.product_version === bugExportVersion
        )?.report_count ?? 0;
      setNotice(`Exported ${count} bug report${count === 1 ? "" : "s"} as JSON.`);
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setExportingBugs(false);
    }
  };

  const changeTicketStatus = async () => {
    if (!ticketDetail) return;
    try {
      const updated = await api.setTicketStatus(
        ticketDetail.id,
        ticketDetail.resolved ? "open" : "closed",
        ticketDetail.updated_at,
        csrf
      );
      setTicketDetail(updated);
      setNotice(updated.resolved ? "Ticket closed." : "Ticket reopened.");
      await refreshAll();
    } catch (requestError) {
      if (axios.isAxiosError(requestError) && requestError.response?.status === 409) {
        setError("Conflict: another administrator changed this ticket. The latest version was loaded.");
        setTicketDetail(await api.ticket(ticketDetail.id));
      } else setError(errorMessage(requestError));
    }
  };

  const changeBugStatus = async () => {
    if (!bugDetail) return;
    try {
      const updated = await api.setBugReportStatus(
        bugDetail.id,
        bugDetail.resolved ? "open" : "closed",
        bugDetail.updated_at,
        csrf
      );
      setBugDetail(updated);
      setNotice(updated.resolved ? "Bug report closed." : "Bug report reopened.");
      await refreshAll();
    } catch (requestError) {
      if (axios.isAxiosError(requestError) && requestError.response?.status === 409) {
        setError("Conflict: another administrator changed this report. The latest version was loaded.");
        setBugDetail(await api.bugReport(bugDetail.id));
      } else setError(errorMessage(requestError));
    }
  };

  const deliverReply = async (previous?: SupportReply) => {
    if (!ticketDetail) return;
    const responseText = previous?.response_text ?? replyText;
    if (previous) {
      const proceed = window.confirm(
        "This earlier attempt may have been delivered. Retrying can send a duplicate. Send a new explicit attempt?"
      );
      if (!proceed) return;
    }
    const attempt =
      pendingReplyAttempt &&
        pendingReplyAttempt.ticketId === ticketDetail.id &&
        pendingReplyAttempt.responseText === responseText &&
        pendingReplyAttempt.closeAfterSend === (previous?.close_after_send ?? closeAfterSend) &&
        pendingReplyAttempt.previousReplyId === previous?.id
        ? pendingReplyAttempt
        : {
          ticketId: ticketDetail.id,
          responseText,
          closeAfterSend: previous?.close_after_send ?? closeAfterSend,
          previousReplyId: previous?.id,
          idempotencyKey: uuid(),
        };
    setPendingReplyAttempt(attempt);
    setSending(true);
    setError("");
    try {
      const result = await api.sendReply(
        ticketDetail.id,
        responseText,
        attempt.closeAfterSend,
        attempt.idempotencyKey,
        csrf,
        attempt.previousReplyId
      );
      setPendingReplyAttempt(null);
      setNotice(
        result.status === "sent"
          ? "Reply accepted for delivery."
          : result.status === "unknown"
            ? "Delivery is uncertain. The ticket remains open; do not retry without checking."
            : "The email was not accepted. The ticket remains open."
      );
      setReplyText("");
      setTicketDetail(await api.ticket(ticketDetail.id));
      await refreshAll();
    } catch (requestError) {
      const responseStatus = axios.isAxiosError(requestError)
        ? requestError.response?.status
        : undefined;
      if (responseStatus !== undefined && responseStatus < 500) {
        setPendingReplyAttempt(null);
      } else {
        setError(
          "The request result could not be confirmed. Retry the same attempt to check its existing result safely."
        );
        return;
      }
      setError(errorMessage(requestError));
    } finally {
      setSending(false);
      setConfirmSend(false);
    }
  };

  const deliverBugReply = async (previous?: SupportReply) => {
    if (!bugDetail) return;
    const responseText = previous?.response_text ?? bugReplyText;
    if (previous) {
      const proceed = window.confirm(
        "This earlier attempt may have been delivered. Retrying can send a duplicate. Send a new explicit attempt?"
      );
      if (!proceed) return;
    }
    const attempt =
      pendingBugReplyAttempt &&
        pendingBugReplyAttempt.reportId === bugDetail.id &&
        pendingBugReplyAttempt.responseText === responseText &&
        pendingBugReplyAttempt.closeAfterSend ===
        (previous?.close_after_send ?? bugCloseAfterSend) &&
        pendingBugReplyAttempt.previousReplyId === previous?.id
        ? pendingBugReplyAttempt
        : {
          reportId: bugDetail.id,
          responseText,
          closeAfterSend: previous?.close_after_send ?? bugCloseAfterSend,
          previousReplyId: previous?.id,
          idempotencyKey: uuid(),
        };
    setPendingBugReplyAttempt(attempt);
    setBugSending(true);
    setError("");
    try {
      const result = await api.sendBugReportReply(
        bugDetail.id,
        responseText,
        attempt.closeAfterSend,
        attempt.idempotencyKey,
        csrf,
        attempt.previousReplyId
      );
      setPendingBugReplyAttempt(null);
      setNotice(
        result.status === "sent"
          ? "Reply accepted for delivery."
          : result.status === "unknown"
            ? "Delivery is uncertain. The bug report remains open; do not retry without checking."
            : "The email was not accepted. The bug report remains open."
      );
      setBugReplyText("");
      setBugDetail(await api.bugReport(bugDetail.id));
      await refreshAll();
    } catch (requestError) {
      const responseStatus = axios.isAxiosError(requestError)
        ? requestError.response?.status
        : undefined;
      if (responseStatus !== undefined && responseStatus < 500) {
        setPendingBugReplyAttempt(null);
      } else {
        setError(
          "The request result could not be confirmed. Retry the same attempt to check its existing result safely."
        );
        return;
      }
      setError(errorMessage(requestError));
    } finally {
      setBugSending(false);
      setConfirmBugSend(false);
    }
  };

  const executeKeyRequest = async (
    request: { productId: string; maxLicenses: number; idempotencyKey: string },
    csrfToken: string
  ) => {
    setIssuing(true);
    setError("");
    try {
      const result = await api.createProductKey(
        request.productId,
        request.maxLicenses,
        request.idempotencyKey,
        csrfToken
      );
      setClaimKey(result);
      setPendingKeyRequest(null);
      setNotice("One-time claim key created. Copy it now.");
    } catch (requestError) {
      const detail = axios.isAxiosError(requestError) ? requestError.response?.data?.detail : null;
      if (detail?.code === "recent_auth_required") {
        setPendingKeyRequest(request);
        setReauthOpen(true);
      } else {
        const responseStatus = axios.isAxiosError(requestError)
          ? requestError.response?.status
          : undefined;
        if (responseStatus !== undefined && responseStatus < 500) {
          setPendingKeyRequest(null);
        }
        setError(errorMessage(requestError));
      }
    } finally {
      setIssuing(false);
    }
  };

  const requestKey = async () => {
    if (!selectedProduct) return;
    const request =
      pendingKeyRequest ?? {
        productId: selectedProduct,
        maxLicenses,
        idempotencyKey: uuid(),
      };
    const name = catalog.find(item => item.id === request.productId)?.name ?? request.productId;
    const prompt = pendingKeyRequest
      ? `Retry the same ${name} key request and retrieve its existing result?`
      : `Create one unowned ${name} key with ${request.maxLicenses} maximum activation${request.maxLicenses === 1 ? "" : "s"
      }?`;
    if (!window.confirm(prompt)) return;
    setPendingKeyRequest(request);
    await executeKeyRequest(request, csrf);
  };

  const executeDiscountRequest = async (
    request: {
      name: string;
      customCode?: string;
      percentage: number;
      maxRedemptions: number;
      expiresAt: string;
      idempotencyKey: string;
    },
    csrfToken: string
  ) => {
    setCreatingDiscount(true);
    setError("");
    try {
      const result = await api.createDiscountCode(
        request.name,
        request.customCode,
        request.percentage,
        request.maxRedemptions,
        request.expiresAt,
        request.idempotencyKey,
        csrfToken
      );
      setDiscountCode(result);
      setPendingDiscountRequest(null);
      setNotice("Temporary discount code created. Copy it now.");
      await loadDiscountCodes();
    } catch (requestError) {
      const detail = axios.isAxiosError(requestError) ? requestError.response?.data?.detail : null;
      if (detail?.code === "recent_auth_required") {
        setPendingDiscountRequest(request);
        setReauthOpen(true);
      } else {
        const responseStatus = axios.isAxiosError(requestError)
          ? requestError.response?.status
          : undefined;
        if (responseStatus !== undefined && responseStatus < 500) {
          setPendingDiscountRequest(null);
        }
        setError(errorMessage(requestError));
      }
    } finally {
      setCreatingDiscount(false);
    }
  };

  const requestDiscount = async () => {
    const normalizedName = discountName.trim();
    const normalizedCustomCode = customDiscountCode.trim() || undefined;
    if (
      !normalizedName ||
      discountPercentage < 1 ||
      discountPercentage > 100 ||
      discountMaxRedemptions < 1 ||
      discountMaxRedemptions > 1000 ||
      (normalizedCustomCode !== undefined && !/^[A-Za-z0-9]+$/.test(normalizedCustomCode))
    ) {
      return;
    }
    const request =
      pendingDiscountRequest ?? {
        name: normalizedName,
        customCode: normalizedCustomCode,
        percentage: discountPercentage,
        maxRedemptions: discountMaxRedemptions,
        expiresAt: new Date(Date.now() + discountDurationHours * 60 * 60 * 1000).toISOString(),
        idempotencyKey: uuid(),
      };
    const prompt = pendingDiscountRequest
      ? `Retry the same ${request.percentage}% discount-code request and retrieve its existing result?`
      : `Create a ${request.percentage}% discount code for ${request.maxRedemptions} redemption${request.maxRedemptions === 1 ? "" : "s"
      } that expires ${formatDate(request.expiresAt)}?`;
    if (!window.confirm(prompt)) return;
    setPendingDiscountRequest(request);
    await executeDiscountRequest(request, csrf);
  };

  const executeDiscountAction = async (
    request: {
      promotionCodeId: string;
      action: "disable" | "delete";
      idempotencyKey: string;
    },
    csrfToken: string
  ) => {
    setDiscountActionBusyId(request.promotionCodeId);
    setError("");
    try {
      if (request.action === "delete") {
        await api.deleteDiscountCode(
          request.promotionCodeId,
          request.idempotencyKey,
          csrfToken
        );
      } else {
        await api.disableDiscountCode(
          request.promotionCodeId,
          request.idempotencyKey,
          csrfToken
        );
      }
      setPendingDiscountAction(null);
      setNotice(
        request.action === "delete" ? "Discount code deleted." : "Discount code disabled."
      );
      await loadDiscountCodes();
    } catch (requestError) {
      const detail = axios.isAxiosError(requestError) ? requestError.response?.data?.detail : null;
      if (detail?.code === "recent_auth_required") {
        setPendingDiscountAction(request);
        setReauthOpen(true);
      } else {
        const responseStatus = axios.isAxiosError(requestError)
          ? requestError.response?.status
          : undefined;
        if (responseStatus !== undefined && responseStatus < 500) {
          setPendingDiscountAction(null);
        }
        setError(errorMessage(requestError));
      }
    } finally {
      setDiscountActionBusyId("");
    }
  };

  const requestDiscountAction = async (
    promotionCodeId: string,
    action: "disable" | "delete"
  ) => {
    const request =
      pendingDiscountAction?.promotionCodeId === promotionCodeId &&
        pendingDiscountAction.action === action
        ? pendingDiscountAction
        : { promotionCodeId, action, idempotencyKey: uuid() };
    const prompt =
      action === "delete"
        ? "Delete this discount? The customer code will be disabled and its backing coupon deleted. Stripe will retain an audit record."
        : "Disable this customer code? It will stop accepting new redemptions but remain in the overview.";
    if (!window.confirm(prompt)) return;
    setPendingDiscountAction(request);
    await executeDiscountAction(request, csrf);
  };

  const reauthenticate = async (event: FormEvent) => {
    event.preventDefault();
    const keyRequest = pendingKeyRequest;
    const discountRequest = pendingDiscountRequest;
    const discountAction = pendingDiscountAction;
    if (!keyRequest && !discountRequest && !discountAction) return;
    if (keyRequest) setIssuing(true);
    if (discountRequest) setCreatingDiscount(true);
    if (discountAction) setDiscountActionBusyId(discountAction.promotionCodeId);
    try {
      const updatedSession = await api.reauthenticate(reauthPassword, csrf);
      onSession(updatedSession);
      setReauthPassword("");
      setReauthOpen(false);
      if (keyRequest) {
        await executeKeyRequest(keyRequest, updatedSession.csrf_token);
      } else if (discountRequest) {
        await executeDiscountRequest(discountRequest, updatedSession.csrf_token);
      } else if (discountAction) {
        await executeDiscountAction(discountAction, updatedSession.csrf_token);
      }
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setIssuing(false);
      setCreatingDiscount(false);
      setDiscountActionBusyId("");
    }
  };

  const cards = useMemo(
    () => [
      ["Open support tickets", summary?.open_tickets ?? "—"],
      ["Open bug reports", summary?.open_bug_reports ?? "—"],
      ["Closed tickets", summary?.closed_tickets ?? "—"],
      ["Closed bug reports", summary?.closed_bug_reports ?? "—"],
    ],
    [summary]
  );
  const normalizedCustomDiscountCode = customDiscountCode.trim();
  const customDiscountCodeValid =
    !normalizedCustomDiscountCode || /^[A-Za-z0-9]+$/.test(normalizedCustomDiscountCode);
  const discountFormValid =
    discountName.trim().length > 0 &&
    discountName.trim().length <= 40 &&
    normalizedCustomDiscountCode.length <= 32 &&
    customDiscountCodeValid &&
    discountPercentage >= 1 &&
    discountPercentage <= 100 &&
    discountMaxRedemptions >= 1 &&
    discountMaxRedemptions <= 1000;

  return (
    <Box
      component="main"
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
        backgroundImage: `linear-gradient(180deg, ${alpha(brand.accent, 0.032)}, transparent 48%)`,
      }}
    >
      <AppBar position="sticky" color="transparent" elevation={0}>
        <Toolbar sx={{ minHeight: { xs: 58, sm: 64 }, px: { xs: 2, sm: 3 } }}>
          <Box component="img" src={logo} alt="WaveMind" sx={{ width: { xs: 68, sm: 92 }, height: "auto", opacity: 0.9 }} />
          <Divider orientation="vertical" flexItem sx={{ mx: { xs: 1, sm: 2.25 }, my: 2 }} />
          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            <Typography variant="overline" color="secondary.main" sx={{ display: "block", "@media (max-width: 359px)": { display: "none" } }}>
              Private support
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: { xs: "none", sm: "block" } }}>
              Support operations workspace
            </Typography>
          </Box>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Avatar
              aria-hidden="true"
              sx={{ width: 32, height: 32, bgcolor: "action.selected", color: "secondary.light", fontSize: 13, fontWeight: 650 }}
            >
              {administrator.display_name.charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ display: { xs: "none", sm: "block" }, minWidth: 0 }}>
              <Typography variant="body2" noWrap sx={{ fontWeight: 620 }}>
                {administrator.display_name}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: "capitalize" }}>
                {administrator.role}
              </Typography>
            </Box>
            <Tooltip title="Sign out">
              <IconButton color="inherit" onClick={signOut} aria-label="Sign out of support">
                <LogoutIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Toolbar>
      </AppBar>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "224px minmax(0, 1fr)" },
          gridTemplateRows: { xs: "auto minmax(0, 1fr)", md: "minmax(0, 1fr)" },
          minHeight: { xs: "calc(100vh - 58px)", sm: "calc(100vh - 64px)" },
        }}
      >
        <Box
          component="nav"
          aria-label="Support workspace navigation"
          sx={{
            minWidth: 0,
            borderRight: { md: 1 },
            borderBottom: { xs: 1, md: 0 },
            borderColor: "divider",
            bgcolor: "background.paper",
            p: { xs: 0, md: 2 },
          }}
        >
          <Box sx={{ position: { md: "sticky" }, top: { md: 80 } }}>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ display: { xs: "none", md: "block" }, px: 1.5, mb: 1 }}
            >
              Workspace
            </Typography>
            <Tabs
              value={section}
              onChange={(_event, value) => setSection(value)}
              orientation={desktopNavigation ? "vertical" : "horizontal"}
              variant="scrollable"
              aria-label="Support sections"
              sx={{
                minHeight: 0,
                px: { xs: 1, md: 0 },
                py: { xs: 0.75, md: 0 },
                "& .MuiTabs-flexContainer": { gap: { xs: 0.5, md: 0.75 } },
                "& .MuiTabs-indicator": desktopNavigation
                  ? { left: 0, width: 2 }
                  : { height: 2 },
                "& .MuiTab-root": {
                  flexDirection: "row",
                  gap: 1.25,
                  px: 1.5,
                  py: 1,
                  minWidth: { xs: "max-content", md: "100%" },
                },
                "& .MuiTab-iconWrapper": { mb: "0 !important", mr: "0 !important" },
              }}
            >
              <Tab value="tickets" icon={<ConfirmationNumberOutlinedIcon fontSize="small" />} iconPosition="start" label="Support Tickets" />
              <Tab value="bugs" icon={<BugReportOutlinedIcon fontSize="small" />} iconPosition="start" label="Bug Reports" />
              <Tab value="users" icon={<SearchIcon fontSize="small" />} iconPosition="start" label="User Lookup" />
              <Tab value="licenses" icon={<VpnKeyOutlinedIcon fontSize="small" />} iconPosition="start" label="License Lookup" />
              <Tab value="activations" icon={<SearchIcon fontSize="small" />} iconPosition="start" label="Activation Lookup" />
              <Tab value="keys" icon={<VpnKeyOutlinedIcon fontSize="small" />} iconPosition="start" label="Product Keys" />
              <Tab value="discounts" icon={<LocalOfferOutlinedIcon fontSize="small" />} iconPosition="start" label="Discount Codes" />
            </Tabs>
          </Box>
        </Box>

        <Box sx={{ minWidth: 0, width: "100%", maxWidth: 1600, mx: "auto", p: { xs: 1.5, sm: 3, lg: 4 } }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
            spacing={1.5}
            sx={{ mb: 2.5 }}
          >
            <Box>
              <Typography component="h1" variant="h1">
                Support dashboard
              </Typography>
              <Typography color="text.secondary" variant="body2" sx={{ mt: 0.5 }}>
                {administrator.display_name} · {administrator.role}
              </Typography>
            </Box>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={refreshAll} aria-label="Refresh dashboard">
              Refresh
            </Button>
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", xl: "repeat(4, minmax(0, 1fr))" },
              gap: { xs: 1, sm: 1.5 },
              mb: 2.5,
            }}
          >
            {cards.map(([label, value], index) => (
              <SummaryCard key={String(label)} label={String(label)} value={value} tone={summaryTones[index]} />
            ))}
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")} aria-live="assertive">
              {error}
            </Alert>
          )}
          <Paper
            variant="outlined"
            sx={{
              overflow: "hidden",
              minWidth: 0,
              backgroundImage: `linear-gradient(135deg, ${alpha(brand.accent, 0.032)}, ${alpha(brand.accent, 0.012)})`,
              boxShadow: `0 10px 28px ${alpha("#000000", 0.16)}`,
            }}
          >
            {section === "tickets" && (
              <Box sx={{ p: { xs: 1.5, sm: 2.5 } }}>
                <QueryControls
                  state={ticketState}
                  search={ticketSearch}
                  counts={{ open: summary?.open_tickets, closed: summary?.closed_tickets }}
                  onState={value => { setTicketState(value); setTicketPage(0); }}
                  onSearch={setTicketSearch}
                  onSubmit={() => { setTicketQuery(ticketSearch.trim()); setTicketPage(0); }}
                  label="Search requester name, email, or ticket ID"
                />
                <ResultTable
                  loading={loading}
                  empty={ticketRows.length === 0}
                  mobileChildren={ticketRows.map(row => <MobileTicketCard key={row.id} row={row} onOpen={openTicket} />)}
                >
                  <Table size="small" aria-label="Support tickets" sx={{ minWidth: 1040, tableLayout: "fixed" }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: 220 }}>Requester</TableCell>
                        <TableCell sx={{ width: 315 }}>Message</TableCell>
                        <TableCell sx={{ width: 85 }}>Status</TableCell>
                        <TableCell sx={{ width: 145 }}>Latest activity</TableCell>
                        <TableCell sx={{ width: 145 }}>Closed</TableCell>
                        <TableCell sx={{ width: 145 }}>Created</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {ticketRows.map(row => (
                        <TableRow hover key={row.id} tabIndex={0} onClick={() => openTicket(row.id)} onKeyDown={event => { if (event.key === "Enter") openTicket(row.id); }} sx={{ cursor: "pointer" }}>
                          <TableCell sx={{ maxWidth: 260 }}><Typography variant="body2" noWrap sx={{ fontWeight: 570 }}>{row.name}</Typography><Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>{row.email}</Typography></TableCell>
                          <TableCell sx={{ maxWidth: 420 }}><Tooltip title={row.preview} placement="top-start"><Typography variant="body2" noWrap>{row.preview}</Typography></Tooltip></TableCell>
                          <TableCell><StatusChip resolved={row.resolved} /></TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums", color: "secondary.light" }}>{formatDate(row.latest_activity_at)}</TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums", color: row.resolved_at ? "success.light" : "text.secondary" }}>{row.resolved_at ? formatDate(row.resolved_at) : "—"}</TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>{formatDate(row.created_at)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ResultTable>
                <TablePagination component="div" count={ticketTotal} page={ticketPage} rowsPerPage={25} rowsPerPageOptions={[25]} onPageChange={(_event, page) => setTicketPage(page)} sx={responsivePaginationSx} />
              </Box>
            )}

            {section === "bugs" && (
              <Box sx={{ p: { xs: 1.5, sm: 2.5 } }}>
                <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
                  <Stack spacing={1.75}>
                    <Box>
                      <Typography component="h2" variant="subtitle1" sx={{ fontWeight: 650 }}>
                        Export for LLM analysis
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Downloads every open and closed report for one plugin version as structured JSON. Reporter emails are omitted.
                      </Typography>
                    </Box>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }}>
                      <FormControl size="small" sx={{ minWidth: { sm: 240 }, flex: 1 }} disabled={bugExportOptionsLoading || bugExportProducts.length === 0}>
                        <InputLabel id="bug-export-product-label">Plugin</InputLabel>
                        <Select
                          labelId="bug-export-product-label"
                          label="Plugin"
                          value={bugExportProduct}
                          inputProps={{ "aria-label": "Plugin to export" }}
                          onChange={event => {
                            const product = event.target.value;
                            setBugExportProduct(product);
                            setBugExportVersion(
                              bugExportOptions.find(option => option.product_id === product)?.product_version || ""
                            );
                          }}
                        >
                          {bugExportProducts.map(product => <MenuItem key={product} value={product}>{product}</MenuItem>)}
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: { sm: 190 }, flex: 1 }} disabled={bugExportOptionsLoading || bugExportVersions.length === 0}>
                        <InputLabel id="bug-export-version-label">Version</InputLabel>
                        <Select
                          labelId="bug-export-version-label"
                          label="Version"
                          value={bugExportVersion}
                          inputProps={{ "aria-label": "Plugin version to export" }}
                          onChange={event => setBugExportVersion(event.target.value)}
                        >
                          {bugExportVersions.map(option => (
                            <MenuItem key={option.product_version} value={option.product_version}>
                              {option.product_version} ({option.report_count} {option.report_count === 1 ? "report" : "reports"})
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Button
                        variant="contained"
                        startIcon={exportingBugs ? <CircularProgress size={16} color="inherit" /> : <DownloadOutlinedIcon />}
                        disabled={!bugExportProduct || !bugExportVersion || exportingBugs || bugExportOptionsLoading}
                        onClick={exportBugs}
                        sx={{ whiteSpace: "nowrap", alignSelf: { xs: "stretch", sm: "center" } }}
                      >
                        {exportingBugs ? "Preparing export…" : "Export JSON"}
                      </Button>
                    </Stack>
                    {!bugExportOptionsLoading && bugExportOptions.length === 0 && (
                      <Typography variant="caption" color="text.secondary">No bug reports are available to export.</Typography>
                    )}
                  </Stack>
                </Paper>
                <QueryControls
                  state={bugState}
                  search={bugSearch}
                  counts={{ open: summary?.open_bug_reports, closed: summary?.closed_bug_reports }}
                  onState={value => { setBugState(value); setBugPage(0); }}
                  onSearch={setBugSearch}
                  onSubmit={() => { setBugQuery(bugSearch.trim()); setBugPage(0); }}
                  label="Search email, product, version, OS, or report ID"
                />
                <ResultTable
                  loading={loading}
                  empty={bugRows.length === 0}
                  mobileChildren={bugRows.map(row => <MobileBugCard key={row.id} row={row} onOpen={openBug} />)}
                >
                  <Table size="small" aria-label="Bug reports" sx={{ minWidth: 820 }}>
                    <TableHead><TableRow><TableCell>Reporter</TableCell><TableCell>Product</TableCell><TableCell>Environment</TableCell><TableCell>Status</TableCell><TableCell>Created</TableCell></TableRow></TableHead>
                    <TableBody>{bugRows.map(row => (
                      <TableRow hover key={row.id} tabIndex={0} onClick={() => openBug(row.id)} onKeyDown={event => { if (event.key === "Enter") openBug(row.id); }} sx={{ cursor: "pointer" }}>
                        <TableCell sx={{ overflowWrap: "anywhere" }}>{row.email}</TableCell><TableCell><Typography variant="body2" sx={{ fontWeight: 570 }}>{row.product_id}</Typography><Typography variant="caption" color="text.secondary">{row.product_version}</Typography></TableCell><TableCell>{row.machine_os}</TableCell><TableCell><StatusChip resolved={row.resolved} /></TableCell><TableCell sx={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>{formatDate(row.created_at)}</TableCell>
                      </TableRow>
                    ))}</TableBody>
                  </Table>
                </ResultTable>
                <TablePagination component="div" count={bugTotal} page={bugPage} rowsPerPage={25} rowsPerPageOptions={[25]} onPageChange={(_event, page) => setBugPage(page)} sx={responsivePaginationSx} />
              </Box>
            )}

            {section === "keys" && (
              <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 720 }}>
                <Typography component="h2" variant="h5" sx={{ mb: 2.5 }}>Product claim keys</Typography>
                {administrator.role !== "manager" ? (
                  <Alert severity="info">Only managers can create product keys.</Alert>
                ) : claimKey ? (
                  <Stack spacing={2}>
                    <Alert severity="warning">This is a bearer secret. It is shown only in memory—copy it now and share it securely.</Alert>
                    <Typography>
                      Maximum activations: <strong>{claimKey.max_licenses}</strong>
                    </Typography>
                    <TextField label={`${claimKey.product_name} claim key`} value={claimKey.claim_key} fullWidth InputProps={{ readOnly: true, endAdornment: <Tooltip title="Copy claim key"><IconButton aria-label="Copy claim key" onClick={async () => { await navigator.clipboard.writeText(claimKey.claim_key); setNotice("Claim key copied."); }}><ContentCopyIcon fontSize="small" /></IconButton></Tooltip> }} />
                    <Button onClick={() => setClaimKey(null)} sx={{ alignSelf: "flex-start" }}>Done and clear key</Button>
                  </Stack>
                ) : (
                  <Stack spacing={2.5}>
                    <Alert severity="info">Creating a key requires a password check within the last 15 minutes.</Alert>
                    {pendingKeyRequest && <Alert severity="warning">A previous key request has no visible result. Retry it with the same identifier before starting another.</Alert>}
                    <FormControl fullWidth disabled={Boolean(pendingKeyRequest)}><InputLabel id="support-product-label">Product</InputLabel><Select labelId="support-product-label" value={selectedProduct} label="Product" onChange={event => setSelectedProduct(event.target.value)}>{catalog.map(product => <MenuItem key={product.id} value={product.id}>{product.name}</MenuItem>)}</Select></FormControl>
                    <TextField
                      disabled={Boolean(pendingKeyRequest)}
                      label="Maximum activations"
                      helperText="Choose how many devices can activate this license (1–10)."
                      type="number"
                      value={maxLicenses}
                      onChange={event =>
                        setMaxLicenses(Math.max(1, Math.min(10, Number(event.target.value))))
                      }
                      inputProps={{ min: 1, max: 10, step: 1 }}
                    />
                    <Button variant="contained" disabled={!selectedProduct || issuing} onClick={requestKey} sx={{ alignSelf: { sm: "flex-start" } }}>{issuing ? "Creating…" : pendingKeyRequest ? "Retry prior request" : "Confirm and create key"}</Button>
                  </Stack>
                )}
              </Box>
            )}

            {section === "users" && <UserLookupPanel csrf={csrf} initialEmail={userLookupEmail}
              onOpenLicense={licenseKey => { setLicenseLookupKey(licenseKey); setSection("licenses"); }} />}

            {section === "licenses" && <LicenseLookupPanel csrf={csrf} initialLicenseKey={licenseLookupKey}
              onOpenUser={email => { setUserLookupEmail(email); setSection("users"); }} />}

            {section === "activations" && <ActivationLookupPanel csrf={csrf}
              onOpenLicense={licenseKey => { setLicenseLookupKey(licenseKey); setSection("licenses"); }}
              onOpenUser={email => { setUserLookupEmail(email); setSection("users"); }} />}

            {section === "discounts" && (
              <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1120 }}>
                <Typography component="h2" variant="h5" sx={{ mb: 2.5 }}>Temporary discount codes</Typography>
                {administrator.role !== "manager" ? (
                  <Alert severity="info">Only managers can create discount codes.</Alert>
                ) : (
                  <Stack spacing={4}>
                    <Box sx={{ maxWidth: 720 }}>
                      {discountCode ? (
                        <Stack spacing={2}>
                          <Alert severity="warning">This code can be redeemed up to {discountCode.max_redemptions} time{discountCode.max_redemptions === 1 ? "" : "s"}. Copy it and share it securely.</Alert>
                          <Box>
                            <Typography variant="overline" color="text.secondary">{discountCode.name}</Typography>
                            <Typography>
                              <strong>{discountCode.percentage}% off</strong>{discountCode.expires_at ? ` · Expires ${formatDate(discountCode.expires_at)}` : ""}
                            </Typography>
                          </Box>
                          <TextField
                            label="Customer discount code"
                            value={discountCode.code}
                            fullWidth
                            InputProps={{
                              readOnly: true,
                              endAdornment: (
                                <Tooltip title="Copy discount code">
                                  <IconButton
                                    aria-label="Copy discount code"
                                    onClick={async () => {
                                      await navigator.clipboard.writeText(discountCode.code);
                                      setNotice("Discount code copied.");
                                    }}
                                  >
                                    <ContentCopyIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              ),
                            }}
                          />
                          <Button onClick={() => setDiscountCode(null)} sx={{ alignSelf: "flex-start" }}>Create another code</Button>
                        </Stack>
                      ) : (
                        <Stack spacing={2.5}>
                          <Alert severity="info">Codes expire automatically. Choose a redemption limit from 1 to 1,000; creation and management actions require a recent password check.</Alert>
                          {pendingDiscountRequest && <Alert severity="warning">A previous discount request has no visible result. Retry it with the same identifier before starting another.</Alert>}
                          <TextField
                            required
                            disabled={Boolean(pendingDiscountRequest)}
                            label="Code name"
                            helperText="A short description shown in Stripe and on customer receipts."
                            value={discountName}
                            onChange={event => setDiscountName(event.target.value)}
                            inputProps={{ maxLength: 40 }}
                          />
                          <TextField
                            disabled={Boolean(pendingDiscountRequest)}
                            label="Custom customer code (optional)"
                            helperText={customDiscountCodeValid ? "Leave blank and Stripe will generate a code." : "Use letters and numbers only."}
                            error={!customDiscountCodeValid || normalizedCustomDiscountCode.length > 32}
                            value={customDiscountCode}
                            onChange={event => setCustomDiscountCode(event.target.value)}
                            inputProps={{ maxLength: 32, autoCapitalize: "characters", spellCheck: false }}
                          />
                          <TextField
                            required
                            disabled={Boolean(pendingDiscountRequest)}
                            label="Discount percentage"
                            helperText="Enter a whole percentage from 1 to 100."
                            type="number"
                            value={discountPercentage}
                            onChange={event =>
                              setDiscountPercentage(Math.max(1, Math.min(100, Number(event.target.value))))
                            }
                            inputProps={{ min: 1, max: 100, step: 1 }}
                          />
                          <TextField
                            required
                            disabled={Boolean(pendingDiscountRequest)}
                            label="Maximum redemptions"
                            helperText="How many successful checkouts may use this code (1–1,000)."
                            type="number"
                            value={discountMaxRedemptions}
                            onChange={event =>
                              setDiscountMaxRedemptions(
                                Math.max(1, Math.min(1000, Number(event.target.value)))
                              )
                            }
                            inputProps={{ min: 1, max: 1000, step: 1 }}
                          />
                          <FormControl fullWidth disabled={Boolean(pendingDiscountRequest)}>
                            <InputLabel id="discount-validity-label">Expires after</InputLabel>
                            <Select
                              labelId="discount-validity-label"
                              value={discountDurationHours}
                              label="Expires after"
                              onChange={event => setDiscountDurationHours(Number(event.target.value))}
                            >
                              <MenuItem value={24}>24 hours</MenuItem>
                              <MenuItem value={72}>3 days</MenuItem>
                              <MenuItem value={168}>7 days</MenuItem>
                              <MenuItem value={720}>30 days</MenuItem>
                            </Select>
                          </FormControl>
                          <Button
                            variant="contained"
                            disabled={!discountFormValid || creatingDiscount || Boolean(pendingDiscountAction)}
                            onClick={requestDiscount}
                            sx={{ alignSelf: { sm: "flex-start" } }}
                          >
                            {creatingDiscount ? "Creating…" : pendingDiscountRequest ? "Retry prior request" : "Confirm and create code"}
                          </Button>
                        </Stack>
                      )}
                    </Box>

                    <Divider />
                    <Box>
                      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} gap={1} sx={{ mb: 1.5 }}>
                        <Box>
                          <Typography component="h3" variant="h6">Created codes</Typography>
                          <Typography variant="body2" color="text.secondary">Codes generated from this support workspace, including disabled and deleted records.</Typography>
                        </Box>
                        <Button size="small" startIcon={<RefreshIcon />} onClick={loadDiscountCodes} disabled={discountsLoading}>Refresh codes</Button>
                      </Stack>
                      {pendingDiscountAction && <Alert severity="warning" sx={{ mb: 1.5 }}>A previous management request has no confirmed result. Retry the same action before starting another.</Alert>}
                      {discountsLoading ? (
                        <Box sx={{ display: "grid", placeItems: "center", minHeight: 120 }}><CircularProgress size={28} /></Box>
                      ) : discountRows.length === 0 ? (
                        <Paper variant="outlined" sx={{ p: 2.5 }}><Typography color="text.secondary">No support discount codes have been created yet.</Typography></Paper>
                      ) : (
                        <TableContainer component={Paper} variant="outlined">
                          <Table size="small" aria-label="Created discount codes" sx={{ minWidth: 900 }}>
                            <TableHead><TableRow><TableCell>Code</TableCell><TableCell>Name</TableCell><TableCell>Discount</TableCell><TableCell>Redemptions</TableCell><TableCell>Expires</TableCell><TableCell>Status</TableCell><TableCell align="right">Actions</TableCell></TableRow></TableHead>
                            <TableBody>
                              {discountRows.map(row => {
                                const rowStatus = discountCodeStatus(row);
                                const rowBusy = discountActionBusyId === row.id;
                                const disableRetry =
                                  pendingDiscountAction?.promotionCodeId === row.id &&
                                  pendingDiscountAction.action === "disable";
                                const deleteRetry =
                                  pendingDiscountAction?.promotionCodeId === row.id &&
                                  pendingDiscountAction.action === "delete";
                                return (
                                  <TableRow key={row.id}>
                                    <TableCell><Stack direction="row" alignItems="center" spacing={0.5}><Typography variant="body2" sx={{ fontFamily: "monospace", fontWeight: 650 }}>{row.code}</Typography><IconButton size="small" aria-label={`Copy discount code ${row.code}`} onClick={async () => { await navigator.clipboard.writeText(row.code); setNotice("Discount code copied."); }}><ContentCopyIcon sx={{ fontSize: 16 }} /></IconButton></Stack></TableCell>
                                    <TableCell>{row.name}</TableCell>
                                    <TableCell>{row.percentage}%</TableCell>
                                    <TableCell>{row.times_redeemed} / {row.max_redemptions}</TableCell>
                                    <TableCell sx={{ whiteSpace: "nowrap" }}>{row.expires_at ? formatDate(row.expires_at) : "—"}</TableCell>
                                    <TableCell><Chip size="small" color={rowStatus === "Active" ? "success" : rowStatus === "Deleted" ? "default" : "warning"} label={rowStatus} /></TableCell>
                                    <TableCell align="right">
                                      <Stack direction="row" justifyContent="flex-end" spacing={0.5}>
                                        {rowStatus === "Active" && <Button size="small" startIcon={<BlockOutlinedIcon />} disabled={Boolean(discountActionBusyId) || Boolean(pendingDiscountRequest) || (Boolean(pendingDiscountAction) && !disableRetry)} onClick={() => requestDiscountAction(row.id, "disable")}>{rowBusy ? "Working…" : disableRetry ? "Retry disable" : "Disable"}</Button>}
                                        {!row.deleted && <Button size="small" color="error" startIcon={<DeleteOutlineIcon />} disabled={Boolean(discountActionBusyId) || Boolean(pendingDiscountRequest) || (Boolean(pendingDiscountAction) && !deleteRetry)} onClick={() => requestDiscountAction(row.id, "delete")}>{rowBusy ? "Working…" : deleteRetry ? "Retry delete" : "Delete"}</Button>}
                                      </Stack>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </Box>
                  </Stack>
                )}
              </Box>
            )}
          </Paper>
        </Box>
      </Box>

      <Dialog
        open={Boolean(ticketDetail)}
        onClose={() => !sending && setTicketDetail(null)}
        fullWidth
        maxWidth="md"
        aria-labelledby="ticket-title"
        PaperProps={{ sx: { m: { xs: 1.25, sm: 4 }, width: { xs: "calc(100% - 20px)", sm: "calc(100% - 64px)" }, maxWidth: { xs: "calc(100% - 20px)", sm: 900 }, maxHeight: { xs: "calc(100% - 20px)", sm: "calc(100% - 64px)" } } }}
      >
        {ticketDetail && (
          <>
            <DialogTitle id="ticket-title" sx={{ pr: 7 }}>
              Ticket {ticketDetail.id.slice(0, 8).toUpperCase()}
              <IconButton onClick={() => setTicketDetail(null)} aria-label="Close ticket detail" sx={{ position: "absolute", right: 14, top: 13 }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2.5}>
                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} gap={1}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h6">{ticketDetail.name}</Typography>
                    <Typography color="text.secondary" variant="body2" sx={{ overflowWrap: "anywhere" }}>{ticketDetail.email}</Typography>
                  </Box>
                  <StatusChip resolved={ticketDetail.resolved} />
                </Stack>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" }, gap: 1 }}>
                  <TicketTimestamp label="Created" value={formatDate(ticketDetail.created_at)} accent={brand.violet} />
                  <TicketTimestamp label="Latest activity" value={formatDate(ticketDetail.latest_activity_at)} accent={brand.cyan} />
                  <TicketTimestamp label="Closed" value={ticketDetail.resolved_at ? formatDate(ticketDetail.resolved_at) : "Not closed"} accent={brand.emerald} />
                </Box>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: "rgba(7, 9, 14, 0.36)" }}>
                  <Typography variant="overline" color="text.secondary">Original request</Typography>
                  <Typography sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", mt: 0.75 }}>{ticketDetail.content}</Typography>
                </Paper>
                <Box>
                  <Typography variant="h6" sx={{ mb: 1.25 }}>Reply history</Typography>
                  {ticketDetail.replies.length === 0 ? (
                    <Typography color="text.secondary">No replies yet.</Typography>
                  ) : (
                    <Stack spacing={1.25}>
                      {ticketDetail.replies.map(reply => (
                        <Paper variant="outlined" sx={{ p: 2 }} key={reply.id}>
                          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} gap={1}>
                            <Typography fontWeight={600}>{reply.administrator_display_name}</Typography>
                            <Chip size="small" color={reply.status === "sent" ? "success" : reply.status === "failed" || reply.status === "unknown" ? "warning" : "default"} label={replyStatusLabel(reply.status)} />
                          </Stack>
                          <Typography variant="caption" color="text.secondary">{formatDate(reply.created_at)}</Typography>
                          <Typography sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", mt: 1 }}>{reply.response_text}</Typography>
                          {(reply.status === "failed" || reply.status === "unknown") && <Button size="small" sx={{ mt: 1 }} disabled={sending} onClick={() => deliverReply(reply)}>Explicit retry…</Button>}
                        </Paper>
                      ))}
                    </Stack>
                  )}
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ mb: 1.25 }}>Compose response</Typography>
                  {pendingReplyAttempt?.ticketId === ticketDetail.id && <Alert severity="warning" sx={{ mb: 2 }}>A previous request has no confirmed result. Keep this content unchanged and retry to retrieve the existing attempt safely.</Alert>}
                  <TextField label="Response" multiline minRows={6} fullWidth disabled={Boolean(pendingReplyAttempt)} value={replyText} inputProps={{ maxLength: 20000 }} onChange={event => setReplyText(event.target.value)} />
                  <FormControlLabel sx={{ mt: 0.75, alignItems: "flex-start", "& .MuiCheckbox-root": { pt: 0.5 } }} control={<Checkbox disabled={Boolean(pendingReplyAttempt)} checked={closeAfterSend} onChange={event => setCloseAfterSend(event.target.checked)} />} label="Mark ticket closed after the email is accepted" />
                </Box>
              </Stack>
            </DialogContent>
            <DialogActions sx={{ flexDirection: { xs: "column-reverse", sm: "row" }, alignItems: { xs: "stretch", sm: "center" }, "& .MuiButton-root": { width: { xs: "100%", sm: "auto" }, m: { xs: "0 !important", sm: undefined } } }}>
              <Button onClick={changeTicketStatus}>{ticketDetail.resolved ? "Reopen" : "Close without reply"}</Button>
              <Button variant="contained" disabled={sending || !replyText.trim()} onClick={() => setConfirmSend(true)}>{sending ? "Sending…" : pendingReplyAttempt ? "Check prior send result" : "Review and send"}</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Dialog open={confirmSend} onClose={() => !sending && setConfirmSend(false)} aria-labelledby="confirm-send-title" fullWidth maxWidth="xs" PaperProps={{ sx: { m: { xs: 1.25, sm: 4 }, width: { xs: "calc(100% - 20px)", sm: "calc(100% - 64px)" }, maxWidth: { xs: "calc(100% - 20px)", sm: 444 } } }}>
        <DialogTitle id="confirm-send-title">Send support email?</DialogTitle>
        <DialogContent><Typography>The response will be emailed to {ticketDetail?.email}. SMTP acceptance means accepted for delivery, not proof of receipt.</Typography></DialogContent>
        <DialogActions sx={{ flexDirection: { xs: "column-reverse", sm: "row" }, "& .MuiButton-root": { width: { xs: "100%", sm: "auto" } } }}><Button onClick={() => setConfirmSend(false)}>Cancel</Button><Button variant="contained" disabled={sending} onClick={() => deliverReply()}>Send email</Button></DialogActions>
      </Dialog>

      <Dialog open={Boolean(bugDetail)} onClose={() => !bugSending && setBugDetail(null)} fullWidth maxWidth="md" aria-labelledby="bug-title" PaperProps={{ sx: { m: { xs: 1.25, sm: 4 }, width: { xs: "calc(100% - 20px)", sm: "calc(100% - 64px)" }, maxWidth: { xs: "calc(100% - 20px)", sm: 900 }, maxHeight: { xs: "calc(100% - 20px)", sm: "calc(100% - 64px)" } } }}>
        {bugDetail && (
          <>
            <DialogTitle id="bug-title" sx={{ pr: 7 }}>
              Bug report {bugDetail.id.slice(0, 8).toUpperCase()}
              <IconButton disabled={bugSending} onClick={() => setBugDetail(null)} aria-label="Close bug report detail" sx={{ position: "absolute", right: 14, top: 13 }}><CloseIcon fontSize="small" /></IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2.5}>
                <StatusChip resolved={bugDetail.resolved} />
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "120px minmax(0, 1fr)" }, gap: 1 }}>
                  <Typography color="text.secondary" variant="body2">Reporter</Typography><Typography sx={{ overflowWrap: "anywhere" }}>{bugDetail.email}</Typography>
                  <Typography color="text.secondary" variant="body2">Product</Typography><Typography>{bugDetail.product_id} · {bugDetail.product_version}</Typography>
                  <Typography color="text.secondary" variant="body2">OS</Typography><Typography>{bugDetail.machine_os}</Typography>
                </Box>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: "rgba(7, 9, 14, 0.36)" }}>
                  <Typography variant="overline" color="text.secondary">Original bug report</Typography>
                  <Typography sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", mt: 0.75 }}>{bugDetail.content}</Typography>
                </Paper>
                <Box>
                  <Typography variant="h6" sx={{ mb: 1.25 }}>Reply history</Typography>
                  {bugDetail.replies.length === 0 ? (
                    <Typography color="text.secondary">No replies yet.</Typography>
                  ) : (
                    <Stack spacing={1.25}>
                      {bugDetail.replies.map(reply => (
                        <Paper variant="outlined" sx={{ p: 2 }} key={reply.id}>
                          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} gap={1}>
                            <Typography fontWeight={600}>{reply.administrator_display_name}</Typography>
                            <Chip size="small" color={reply.status === "sent" ? "success" : reply.status === "failed" || reply.status === "unknown" ? "warning" : "default"} label={replyStatusLabel(reply.status)} />
                          </Stack>
                          <Typography variant="caption" color="text.secondary">{formatDate(reply.created_at)}</Typography>
                          <Typography sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", mt: 1 }}>{reply.response_text}</Typography>
                          {(reply.status === "failed" || reply.status === "unknown") && <Button size="small" sx={{ mt: 1 }} disabled={bugSending} onClick={() => deliverBugReply(reply)}>Explicit retry…</Button>}
                        </Paper>
                      ))}
                    </Stack>
                  )}
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ mb: 1.25 }}>Compose response</Typography>
                  {pendingBugReplyAttempt?.reportId === bugDetail.id && <Alert severity="warning" sx={{ mb: 2 }}>A previous request has no confirmed result. Keep this content unchanged and retry to retrieve the existing attempt safely.</Alert>}
                  <TextField label="Response" multiline minRows={6} fullWidth disabled={Boolean(pendingBugReplyAttempt)} value={bugReplyText} inputProps={{ maxLength: 20000 }} onChange={event => setBugReplyText(event.target.value)} />
                  <FormControlLabel sx={{ mt: 0.75, alignItems: "flex-start", "& .MuiCheckbox-root": { pt: 0.5 } }} control={<Checkbox disabled={Boolean(pendingBugReplyAttempt)} checked={bugCloseAfterSend} onChange={event => setBugCloseAfterSend(event.target.checked)} />} label="Mark bug report closed after the email is accepted" />
                </Box>
              </Stack>
            </DialogContent>
            <DialogActions sx={{ flexDirection: { xs: "column-reverse", sm: "row" }, alignItems: { xs: "stretch", sm: "center" }, "& .MuiButton-root": { width: { xs: "100%", sm: "auto" }, m: { xs: "0 !important", sm: undefined } } }}>
              <Button disabled={bugSending} onClick={changeBugStatus}>{bugDetail.resolved ? "Reopen report" : "Close without reply"}</Button>
              <Button variant="contained" disabled={bugSending || !bugReplyText.trim()} onClick={() => setConfirmBugSend(true)}>{bugSending ? "Sending…" : pendingBugReplyAttempt ? "Check prior send result" : "Review and send"}</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Dialog open={confirmBugSend} onClose={() => !bugSending && setConfirmBugSend(false)} aria-labelledby="confirm-bug-send-title" fullWidth maxWidth="xs" PaperProps={{ sx: { m: { xs: 1.25, sm: 4 }, width: { xs: "calc(100% - 20px)", sm: "calc(100% - 64px)" }, maxWidth: { xs: "calc(100% - 20px)", sm: 444 } } }}>
        <DialogTitle id="confirm-bug-send-title">Send bug report email?</DialogTitle>
        <DialogContent><Typography>The response will be emailed to {bugDetail?.email}. SMTP acceptance means accepted for delivery, not proof of receipt.</Typography></DialogContent>
        <DialogActions sx={{ flexDirection: { xs: "column-reverse", sm: "row" }, "& .MuiButton-root": { width: { xs: "100%", sm: "auto" } } }}><Button onClick={() => setConfirmBugSend(false)}>Cancel</Button><Button variant="contained" disabled={bugSending} onClick={() => deliverBugReply()}>Send email</Button></DialogActions>
      </Dialog>

      <Dialog open={reauthOpen} onClose={() => !issuing && !creatingDiscount && !discountActionBusyId && setReauthOpen(false)} aria-labelledby="reauth-title" component="form" onSubmit={reauthenticate} fullWidth maxWidth="xs" PaperProps={{ sx: { m: { xs: 1.25, sm: 4 }, width: { xs: "calc(100% - 20px)", sm: "calc(100% - 64px)" }, maxWidth: { xs: "calc(100% - 20px)", sm: 444 } } }}>
        <DialogTitle id="reauth-title">Confirm your password</DialogTitle>
        <DialogContent><Typography color="text.secondary" sx={{ mb: 2 }}>Product keys and discount codes require recent manager authentication.</Typography><TextField autoFocus required fullWidth type="password" label="Password" autoComplete="current-password" value={reauthPassword} onChange={event => setReauthPassword(event.target.value)} /></DialogContent>
        <DialogActions sx={{ flexDirection: { xs: "column-reverse", sm: "row" }, "& .MuiButton-root": { width: { xs: "100%", sm: "auto" } } }}><Button onClick={() => setReauthOpen(false)}>Cancel</Button><Button type="submit" variant="contained" disabled={issuing || creatingDiscount || Boolean(discountActionBusyId)}>Continue</Button></DialogActions>
      </Dialog>

      <Snackbar anchorOrigin={{ vertical: "bottom", horizontal: "center" }} open={Boolean(notice)} autoHideDuration={7000} onClose={() => setNotice("")} message={notice} ContentProps={{ role: "status", "aria-live": "polite" }} />
    </Box>
  );
}

function SummaryCard({ label, value, tone }: { label: string; value: string | number; tone: SummaryTone }) {
  const toneColor = summaryToneColors[tone];
  return (
    <Card
      sx={{
        position: "relative",
        overflow: "hidden",
        borderColor: alpha(toneColor, 0.28),
        backgroundImage: `linear-gradient(135deg, ${alpha(toneColor, 0.105)}, ${alpha(toneColor, 0.04)})`,
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          position: "absolute",
          inset: "0 auto 0 0",
          width: 3,
          backgroundColor: alpha(toneColor, 0.78),
        }}
      />
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ mb: 0.75 }}>
          <Typography color="text.secondary" variant="body2">{label}</Typography>
          <Box aria-hidden="true" sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: toneColor }} />
        </Stack>
        <Typography component="p" variant="h4">{value}</Typography>
      </CardContent>
    </Card>
  );
}

function MobileTicketCard({ row, onOpen }: { row: TicketSummary; onOpen: (id: string) => void }) {
  return (
    <ButtonBase
      component="button"
      focusRipple
      aria-label={`Open ticket from ${row.name}`}
      onClick={() => onOpen(row.id)}
      sx={{ display: "block", width: "100%", borderRadius: 1, textAlign: "left" }}
    >
      <Paper variant="outlined" sx={{ width: "100%", p: 1.5 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1.25}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" noWrap sx={{ fontWeight: 650 }}>{row.name}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", overflowWrap: "anywhere" }}>{row.email}</Typography>
          </Box>
          <StatusChip resolved={row.resolved} />
        </Stack>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 1.25, display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: 2, overflow: "hidden" }}
        >
          {row.preview}
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1, mt: 1.5 }}>
          <MobileTimestamp label="Latest activity" value={formatDate(row.latest_activity_at)} color={brand.cyan} />
          <MobileTimestamp label="Closed" value={row.resolved_at ? formatDate(row.resolved_at) : "Not closed"} color={brand.emerald} />
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1, fontVariantNumeric: "tabular-nums" }}>
          Created {formatDate(row.created_at)}
        </Typography>
      </Paper>
    </ButtonBase>
  );
}

function MobileBugCard({ row, onOpen }: { row: BugReportSummary; onOpen: (id: string) => void }) {
  return (
    <ButtonBase
      component="button"
      focusRipple
      aria-label={`Open bug report from ${row.email}`}
      onClick={() => onOpen(row.id)}
      sx={{ display: "block", width: "100%", borderRadius: 1, textAlign: "left" }}
    >
      <Paper variant="outlined" sx={{ width: "100%", p: 1.5 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1.25}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 650, overflowWrap: "anywhere" }}>{row.email}</Typography>
            <Typography variant="caption" color="text.secondary">{row.product_id} · {row.product_version}</Typography>
          </Box>
          <StatusChip resolved={row.resolved} />
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.25, display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: 2, overflow: "hidden" }}>{row.preview}</Typography>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-end" spacing={1} sx={{ mt: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>{row.machine_os}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, fontVariantNumeric: "tabular-nums" }}>{formatDate(row.created_at)}</Typography>
        </Stack>
      </Paper>
    </ButtonBase>
  );
}

function MobileTimestamp({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <Box sx={{ minWidth: 0, borderLeft: 2, borderColor: alpha(color, 0.7), pl: 1 }}>
      <Typography variant="overline" color="text.secondary" sx={{ display: "block", lineHeight: 1.3 }}>{label}</Typography>
      <Typography variant="caption" sx={{ display: "block", mt: 0.25, color: value === "Not closed" ? "text.secondary" : color, fontVariantNumeric: "tabular-nums" }}>{value}</Typography>
    </Box>
  );
}

function TicketTimestamp({ label, value, accent }: { label: string; value: string; accent: string }) {
  return (
    <Box sx={{ p: 1.5, border: 1, borderColor: alpha(accent, 0.22), borderRadius: 1, bgcolor: alpha(accent, 0.045) }}>
      <Typography variant="overline" color="text.secondary" sx={{ display: "block" }}>{label}</Typography>
      <Typography variant="body2" sx={{ mt: 0.25, color: value === "Not closed" ? "text.secondary" : accent, fontVariantNumeric: "tabular-nums" }}>{value}</Typography>
    </Box>
  );
}

function QueryControls({ state, search, counts, onState, onSearch, onSubmit, label }: { state: TicketState; search: string; counts: { open?: number; closed?: number }; onState: (state: TicketState) => void; onSearch: (value: string) => void; onSubmit: () => void; label: string }) {
  const count = (value?: number) => value === undefined ? "…" : value;
  return (
    <Stack
      component="form"
      direction={{ xs: "column", md: "row" }}
      spacing={1.25}
      sx={{ mb: 2 }}
      onSubmit={(event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <FormControl sx={{ minWidth: { md: 158 } }}>
        <InputLabel id={`${label}-state-label`}>Status</InputLabel>
        <Select
          labelId={`${label}-state-label`}
          label="Status"
          value={state}
          onChange={event => onState(event.target.value as TicketState)}
        >
          <MenuItem value="open">Open ({count(counts.open)})</MenuItem>
          <MenuItem value="closed">Closed ({count(counts.closed)})</MenuItem>
          <MenuItem value="all">
            All ({counts.open === undefined || counts.closed === undefined ? "…" : counts.open + counts.closed})
          </MenuItem>
        </Select>
      </FormControl>
      <TextField
        fullWidth
        label={label}
        value={search}
        inputProps={{ maxLength: 200 }}
        onChange={event => onSearch(event.target.value)}
      />
      <Button type="submit" variant="contained" color="secondary" startIcon={<SearchIcon />} sx={{ flexShrink: 0 }}>
        Search
      </Button>
    </Stack>
  );
}

function ResultTable({ loading, empty, children, mobileChildren }: { loading: boolean; empty: boolean; children: React.ReactNode; mobileChildren?: React.ReactNode }) {
  if (loading) return <Box sx={{ minHeight: 240, display: "grid", placeItems: "center", border: 1, borderColor: "divider", borderRadius: 1 }}><CircularProgress color="secondary" size={28} aria-label="Loading results" /></Box>;
  if (empty) return <Box sx={{ minHeight: 240, display: "grid", placeItems: "center", textAlign: "center", border: 1, borderColor: "divider", borderRadius: 1 }}><Typography color="text.secondary">No matching items.</Typography></Box>;
  return (
    <>
      {mobileChildren && (
        <Box sx={{ display: { xs: "grid", lg: "none" }, gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }, gap: 1 }}>
          {mobileChildren}
        </Box>
      )}
      <TableContainer sx={{ display: { xs: mobileChildren ? "none" : "block", lg: "block" }, border: 1, borderColor: "divider", borderRadius: 1, maxWidth: "100%" }}>{children}</TableContainer>
    </>
  );
}

function StatusChip({ resolved }: { resolved: boolean }) {
  return <Chip size="small" variant="outlined" color={resolved ? "success" : "secondary"} label={resolved ? "Closed" : "Open"} />;
}

const SupportPortal: React.FC = () => {
  useSupportRobotsMetadata();
  const [checking, setChecking] = useState(true);
  const [session, setSession] = useState<SupportSession | null>(null);

  const clearSession = useCallback(() => setSession(null), []);
  useEffect(() => {
    setInvalidSessionHandler(clearSession);
    api
      .current()
      .then(setSession)
      .catch(() => setSession(null))
      .finally(() => setChecking(false));
    return () => setInvalidSessionHandler(null);
  }, [clearSession]);

  let content: React.ReactNode;
  if (checking) content = <LoadingScreen />;
  else if (!session) content = <Login onLogin={setSession} />;
  else content = <Dashboard session={session} onSession={setSession} onLogout={clearSession} />;

  return (
    <ThemeProvider theme={supportAdminTheme}>
      <ScopedCssBaseline>{content}</ScopedCssBaseline>
    </ThemeProvider>
  );
};

export default SupportPortal;
